import { useAuth } from '@/contexts/AuthContext';
import {
    clampOnboardingCurrentStep,
    createInitialOnboardingData,
    createReferralOnboardingData,
    shouldResetReferralOnboarding,
} from '@/components/Onboarding/onboardingFlow';
import { navigateToOnboardingRoute } from '@/components/Onboarding/inviteRedirect';
import { getShopifyOnboardingResumeWorkspaceId } from '@/contexts/AuthContext/shopifyOnboardingResume';
import { VerifyAuthResponse } from '@common/api/authService';
import { getCurrentProductCode, getProductScopedStorageKey, isPluginProduct } from '@common/api/productContext';
import { useUserStorage } from '@common/hooks/useUserStorage';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldDelayOnboardingRedirect } from './onboardingRedirectGate';
import {
    hasPendingPasswordSetup,
    isAuthPasswordSetupRoute,
} from '@/utils/pendingPasswordSetup';
import { readPaymentReturnWorkspaceId } from '@/utils/paymentReturnWorkspace';
import {
    getPendingWorkspaceReturnId,
    isOnboardingSuccessfulWorkspaceReturn,
} from './AuthContext/workspaceReturnUtils';
import { ONBOARDING_PLAN_RESUME_STEP } from '@/pages/PaymentReturn/paymentReturnFlow';
import { createOperationId } from '@/lib/behaviorTracking';
import { readReferralCode, readReferralEntryId } from '@common/referral/referralAttribution';

type OnboardingCreationSource = 'workspace_switcher' | 'invite_selection';

interface OnboardingData {
  creationSource?: OnboardingCreationSource | 'automatic_onboarding';
  creationRequestId?: string;
  referralCode?: string;
  referralEntryId?: string;
  workspaceId?: string;
  currentStep?: number;
  pendingConnectionRequest?: {
    platformId: string;
    provider: string;
  } | null;
  storeConnectionPlatform?: string | null;
  storeConnectionStoreUrl: string;
  storeConnectionStoreHash: string;
  storeConnectionAccountUuid: string;
  industry: string;
  otherIndustry: string;
  businessName: string;
  logo: File | string | null;
  logoUrl?: string | null;
  workspaceSettings?: Record<string, any> | null;
  connectedStoreName: string;
  connectedStoreLogoUrl?: string | null;
  connectedStoreCount?: number;
  connectedIntegrations: string[];
  connectedIntegrationTrialRequirements?: Record<string, 'checkout_required'>;
  trialEligibilityState?: string | null;
  intent?: string;
  otherIntent?: string;
  plan: string;
  billingCycle: string;
  isTrialStarted: boolean;
  resumePlanAfterCheckout?: boolean;
}

interface OnboardingContextType {
  startOnboarding: (source: OnboardingCreationSource) => void;
  stopOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
  isAcceptingInvite: boolean;
  setIsAcceptingInvite: (isAccepting: boolean) => void;
  // 新增：自动跳转逻辑的控制
  checkOnboardingStatus: (profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

const ONBOARDING_STORAGE_KEY = 'onboarding_state';

const normalizeOnboardingData = (data?: (OnboardingData & { creationIntent?: unknown }) | null): OnboardingData => {
    // Older browser state requested a fresh workspace on every onboarding entry.
    // Discard that legacy flag so the server can safely reuse the latest suspended
    // workspace for the current product.
    const { creationIntent: _legacyCreationIntent, ...savedData } = data || {};
    const nextData = {
        ...createInitialOnboardingData(),
        ...savedData,
    };

    return {
        ...nextData,
        currentStep: clampOnboardingCurrentStep(nextData.currentStep),
    };
};

const isOnboardingPath = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.location.pathname.replace(/\/+$/, '').endsWith('/onboarding');
};

const consumeOnboardingPlanPaymentReturn = (search: string) => {
    const searchParams = new URLSearchParams(search);
    const isPlanResume = searchParams.get('pay_result') === 'cancel'
        && searchParams.get('resume_step') === ONBOARDING_PLAN_RESUME_STEP;

    if (!isPlanResume) {
        return null;
    }

    const workspaceId = searchParams.get('workspace_id')?.trim() || undefined;
    [
        'pay_result',
        'workspace_id',
        'planCode',
        'planName',
        'billingCycle',
        'resume_step',
    ].forEach((key) => searchParams.delete(key));

    return {
        workspaceId,
        nextSearch: searchParams.toString(),
    };
};

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        get, set, remove
    } = useUserStorage();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        profile,
        isLoadingProfile,
        currentWorkspace,
        isLoadingWorkspaces,
    } = useAuth();
    const onboardingStorageKey = getProductScopedStorageKey(
        ONBOARDING_STORAGE_KEY,
        getCurrentProductCode(),
    );
    const isPluginOnboarding = isPluginProduct(getCurrentProductCode());

    const [onboardingData, setOnboardingData] = useState<OnboardingData>(() => {
        const savedState = get<OnboardingData>(onboardingStorageKey);
        const normalizedSavedState = normalizeOnboardingData(savedState);
        const referralCode = isOnboardingPath()
            ? readReferralCode(window.location.search)
            : '';
        const referralEntryId = isOnboardingPath()
            ? readReferralEntryId(window.location.search)
            : '';

        if (shouldResetReferralOnboarding({
            referralCode,
            referralEntryId,
            savedReferralCode: normalizedSavedState.referralCode,
            savedReferralEntryId: normalizedSavedState.referralEntryId,
            isPluginOnboarding,
        })) {
            return createReferralOnboardingData({
                referralCode,
                creationRequestId: `workspace-create-${createOperationId()}`,
                referralEntryId,
            });
        }

        return normalizedSavedState;
    });
    const onboardingDataRef = useRef(onboardingData);
    const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);

    const commitOnboardingData = useCallback((nextData: OnboardingData) => {
        // Persist before enqueueing React state so callers can synchronously
        // observe a storage failure and avoid treating the client state as saved.
        set(onboardingStorageKey, nextData);
        onboardingDataRef.current = nextData;
        setOnboardingData(nextData);
    }, [onboardingStorageKey, set]);

    const updateOnboardingData = useCallback((data: Partial<OnboardingData>) => {
        const nextData = normalizeOnboardingData({
            ...onboardingDataRef.current,
            ...data,
        });
        commitOnboardingData(nextData);
    }, [commitOnboardingData]);

    const startOnboarding = (source: OnboardingCreationSource) => {
        const initialData = normalizeOnboardingData({
            ...createInitialOnboardingData(),
            creationRequestId: `workspace-create-${createOperationId()}`,
            creationSource: source,
        });
        setIsAcceptingInvite(false);
        commitOnboardingData(initialData);

        // 跳转到 onboarding 页面
        if (!isOnboardingPath()) {
            navigateToOnboardingRoute(navigate, window.location.search, window.location.pathname);
        }
    };

    useEffect(() => {
        if (!isOnboardingPath()) {
            setIsAcceptingInvite(false);
            return;
        }

        const checkoutReturn = consumeOnboardingPlanPaymentReturn(location.search);
        if (checkoutReturn) {
            const nextData = normalizeOnboardingData({
                ...onboardingDataRef.current,
                ...(checkoutReturn.workspaceId
                    ? { workspaceId: checkoutReturn.workspaceId }
                    : {}),
                currentStep: 2,
                resumePlanAfterCheckout: true,
            });
            commitOnboardingData(nextData);
            navigate({
                pathname: location.pathname,
                search: checkoutReturn.nextSearch
                    ? `?${checkoutReturn.nextSearch}`
                    : '',
            }, { replace: true });
            return;
        }

        const referralCode = readReferralCode(location.search);
        const referralEntryId = readReferralEntryId(location.search);
        if (shouldResetReferralOnboarding({
            referralCode,
            referralEntryId,
            savedReferralCode: onboardingData.referralCode,
            savedReferralEntryId: onboardingData.referralEntryId,
            isPluginOnboarding,
        })) {
            const nextData = createReferralOnboardingData({
                referralCode,
                creationRequestId: `workspace-create-${createOperationId()}`,
                referralEntryId,
            });
            commitOnboardingData(nextData);
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const resumeWorkspaceId = getShopifyOnboardingResumeWorkspaceId(location.search);
        const resumeStep = searchParams.get('resume_step')?.trim();
        if (!resumeWorkspaceId || (resumeStep && resumeStep !== 'connect')) {
            return;
        }

        const previousData = onboardingDataRef.current;
        if (
            previousData.workspaceId === resumeWorkspaceId
            && previousData.currentStep === 1
        ) {
            return;
        }

        const nextData = normalizeOnboardingData({
            ...createInitialOnboardingData(),
            workspaceId: resumeWorkspaceId,
            currentStep: 1,
        });
        commitOnboardingData(nextData);
    }, [commitOnboardingData, isPluginOnboarding, location.pathname, location.search, navigate, onboardingData.referralCode, onboardingData.referralEntryId]);

    const stopOnboarding = () => {
        remove(onboardingStorageKey);
        setIsAcceptingInvite(false);
        const initialData = createInitialOnboardingData();
        onboardingDataRef.current = initialData;
        setOnboardingData(initialData);
    };

    const nextStep = useCallback(() => {
        const previousData = onboardingDataRef.current;
        const nextData = normalizeOnboardingData({
            ...previousData,
            currentStep: (previousData.currentStep ?? 0) + 1,
        });
        commitOnboardingData(nextData);
    }, [commitOnboardingData]);

    const prevStep = useCallback(() => {
        const previousData = onboardingDataRef.current;
        const nextData = normalizeOnboardingData({
            ...previousData,
            currentStep: (previousData.currentStep ?? 0) - 1,
        });
        commitOnboardingData(nextData);
    }, [commitOnboardingData]);

    // 新增：检查用户是否需要 onboarding
    const checkOnboardingStatus = useCallback((profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => {
        // 未登录，不处理跳转，由 AuthContext 或路由守卫处理
        if(!isLoadingProfile && !profile){
            return;
        }

        if(!isLoadingProfile && profile) {
            const isPluginContext = isPluginProduct(getCurrentProductCode());
            if (isPluginContext && isLoadingWorkspaces) {
                return;
            }

            const hasProductWorkspace = isPluginContext
                ? Boolean(currentWorkspace?.id)
                : Boolean(profile.workspace);

            if (!hasProductWorkspace) {
                const searchParams = new URLSearchParams(window.location.search);
                const pendingOnboardingWorkspaceReturn = isOnboardingSuccessfulWorkspaceReturn(searchParams)
                    && Boolean(getPendingWorkspaceReturnId(searchParams, readPaymentReturnWorkspaceId()));
                if (shouldDelayOnboardingRedirect({
                    profileHasWorkspace: hasProductWorkspace,
                    currentWorkspaceId: currentWorkspace?.id,
                    isLoadingWorkspaces,
                    pendingPasswordSetup: isAuthPasswordSetupRoute(window.location.pathname) && hasPendingPasswordSetup(),
                    pendingWorkspaceReturn: pendingOnboardingWorkspaceReturn,
                    currentPathname: window.location.pathname,
                    search: window.location.search,
                })) {
                    return;
                }

                // 无 workspace，需要 onboarding
                if (!isOnboardingPath()) {
                    navigateToOnboardingRoute(navigate, window.location.search, window.location.pathname);
                }
            }
        }
    }, [currentWorkspace?.id, isLoadingWorkspaces, navigate]);
    
    // Check onboarding status when profile changes
    useEffect(() => {
        checkOnboardingStatus(profile, isLoadingProfile);
    }, [profile, isLoadingProfile, checkOnboardingStatus]);

    const value = {
        startOnboarding,
        stopOnboarding,
        nextStep,
        prevStep,
        onboardingData,
        updateOnboardingData,
        isAcceptingInvite,
        setIsAcceptingInvite,
        checkOnboardingStatus
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
